import { StateType } from '../index';
import { createSelector } from 'reselect';
import * as Fields from '../../components/Account/Data/Fields';
import { exposureFieldDeps, initialAccountDataFieldValues, StagesConfig } from './InitialAccountWizardData';
import { UseFormReturn } from 'react-hook-form/dist/types';
import Immutable from 'immutable';
import { logoutCompetitionIdSelector } from '../CompetitionsReducer/CompetitionsSelector';

// todo: migrate to redux-toolkit
export const accountWizardSelector = (state: StateType) => state.get('accountWizard');
export const dataFieldsSelector = (state: StateType) => state.getIn(['accountWizard', 'dataFields']) as Immutable.Map<any, any>;
export const stageSelector = (state: StateType) => state.getIn(['accountWizard', 'stage']) as number;
export const stageFormSelector = (state: StateType) => state.getIn(['accountWizard', 'stageForm']) as UseFormReturn<any>;
export const submittedStagesSelector = (state: StateType) => state.getIn(['accountWizard', 'submittedStages']);

export const isFinanceStageVisible = (dataFields: Immutable.Map<any, any>) =>
  dataFields.get(Fields.finance.props.name) && dataFields.get(Fields.presentlyEmployed.props.name);
export const stagesCountSelector = createSelector(dataFieldsSelector, (dataFields: any) => {
  let result = 3;

  if (isFinanceStageVisible(dataFields)) {
    result = 4;
  }
  return result;
});
export const currentStageSubmittedSelector = createSelector(
  submittedStagesSelector,
  stageSelector,
  (submittedStages: any, stage: number) => !!submittedStages.get(StagesConfig[stage].name)
);

export const isLastStageSelector = createSelector(stageSelector, stagesCountSelector, (stage, stagesCount) => stage === stagesCount - 1);

export const isFirstStageSelector = createSelector(stageSelector, stage => stage === 0);

export const initSelector = (state: StateType) => state.getIn(['accountWizard', 'isInitialized']);
export const passwordLikeNameErrorSelector = (state: StateType) => state.getIn(['accountWizard', 'errors', 'passwordLikeName']);

export const isStageSubmittedSelector = createSelector(
  submittedStagesSelector,
  // stage is taken from selector parameter in component
  (state: StateType, stage: number) => stage,
  (submittedStages: any, stage) => submittedStages.get(StagesConfig[stage].name)
);

export const isCurrentStageSubmittedSelector = createSelector(
  stageSelector,
  (state: StateType) => state,
  (stage, state) => isStageSubmittedSelector(state, stage)
);

export const countryListSelector = (state: StateType) => state.getIn(Fields.country.props.pathOptions) as any;
export const defaultCountrySelector = (state: StateType) => state.getIn(['account', 'metadata', 'defaultCountry']) as string | undefined;
export const universityListSelector = (state: StateType) => state.getIn(Fields.university.props.pathOptions) as any;
export const genderListSelector = (state: StateType) => state.getIn(Fields.gender.props.pathOptions) as any;

export const errorPopupSelector = (state: StateType) => state.getIn(['accountWizard', 'errorPopup']) as any;
export const mountFieldNamesSelector = createSelector(stageFormSelector, stageForm => {
  return Object.values(stageForm.control._fields)
    .filter(field => field?._f.mount)
    .map(field => field!._f.name);
});

export const accountCountryOptionsMapSelector = (state: StateType) =>
  state.getIn(['account', 'countryOptionsMap']) as Immutable.Map<string, string>;

export const accountOptionsSelector = createSelector(
  (state: StateType) => state.getIn(['account', 'competition']),
  (state: StateType) => state.getIn(['account', 'countryOptionsMap']),
  logoutCompetitionIdSelector,
  (competition, countriesOptions, logoutCompetition) =>
    Immutable.fromJS({
      competition,
      countriesOptions,
      logoutCompetition,
    })
);

export const cleanedDataFieldsSelector = (state: StateType) => {
  let newDataFields = dataFieldsSelector(state) as Immutable.Map<any, any>;

  const referrerToggle = Fields.referrerToggle.props.name;
  const referrer = Fields.referrer.props.name;

  if (!newDataFields.get(referrerToggle)) {
    newDataFields = newDataFields.set(referrer, initialAccountDataFieldValues[referrer]);
  }

  const showExposure = exposureFieldDeps.some(dep => !!newDataFields.get(dep));
  const exposure = Fields.exposureStatus.props.name;
  const exposureDetails = Fields.exposureDetails.props.name;

  if (!showExposure) {
    newDataFields = newDataFields.set(exposure, initialAccountDataFieldValues[exposure]);
    newDataFields = newDataFields.set(exposureDetails, initialAccountDataFieldValues[exposureDetails]);
  }

  const serviceEngagement = Fields.serviceEngagement.props.name;
  const employerServices = Fields.employerServices.props.name;

  if (!newDataFields.get(serviceEngagement)) {
    newDataFields = newDataFields.set(employerServices, initialAccountDataFieldValues[employerServices]);
  }

  const exposureStatus = Fields.exposureStatus.props.name;

  if (!newDataFields.get(exposureStatus)) {
    newDataFields = newDataFields.set(exposureDetails, initialAccountDataFieldValues[exposureDetails]);
  }

  return newDataFields;
};

export const agreementFormStageSelector = createSelector(accountWizardSelector, accountWizard => accountWizard.get('agreementsFormStage'));

export const isReadAgreementsSelector = createSelector(accountWizardSelector, accountWizard => accountWizard.get('isReadAgreements'));

export const protoUserListSelector = createSelector(accountWizardSelector, accountWizard => accountWizard.get('protoUsersList'));
